import ActionBase, { ACTION_TYPES } from './ActionBase.js';

/**
 * @module Core/data/stm/action/RemoveChildAction
 */

/**
 * Action to record store remove child operation.
 * @extends Core/data/stm/action/ActionBase
 */
export default class RemoveChildAction extends ActionBase {
    static $name = 'RemoveChildAction';

    static configurable = {
        /**
         * Reference to a parent model a child model has been removed to.
         *
         * @prp {Core.data.Model}
         * @readonly
         * @default
         */
        parentModel : undefined,

        /**
         * Children models removed.
         *
         * @prp {Core.data.Model[]}
         * @readonly
         * @default
         */
        childModels : undefined,

        /**
         * Map having children models as keys and values containing previous parentIndex.
         *
         * @prp {Object}
         * @readonly
         * @default
         */
        context : undefined,

        stores : undefined
    };

    get type() {
        return ACTION_TYPES.REMOVE_CHILD;
    }

    //<debug>
    afterConfig() {
        super.afterConfig();

        console.assert(
            this.parentModel.isModel &&
            Array.isArray(this.childModels) &&
            this.childModels.every(m => m.isModel) &&
            this.context instanceof Map &&
            Array.isArray(this.stores),
            "Can't create action, bad configuration!"
        );
    }
    //</debug>

    changeParentModel(model) {
        //<debug>
        console.assert(
            !this._parentModel && model,
            "Can't set parent model, model is required and can be set only once!"
        );
        //</debug>

        return model;
    }

    changeChildModels(models) {
        //<debug>
        console.assert(
            !this._childModels &&
            Array.isArray(models) &&
            models.every(m => m.isModel),
            "Can't set child models, models are required, it should be array of Model class and can be set only once!"
        );
        //</debug>

        return models.slice(0);
    }

    changeContext(ctx) {
        //<debug>
        console.assert(
            !this._context &&
            ctx instanceof Map &&
            Array.from(ctx.entries()).every(([k, v]) => {
                return k.isModel && typeof v === 'object' && typeof v.parentIndex === 'number' && typeof v.orderedParentIndex === 'number';
            }),
            "Can't set context, the value is required it should be Map keyed by inserted models with `index` values, and it can be set only once!"
        );
        //</debug>

        return ctx;
    }

    changeStores(stores) {
        //<debug>
        console.assert(Array.isArray(stores) && stores.every(s => s.isStore), 'Stores should be an array of store instances');
        //</debug>

        return stores;
    }

    undo() {
        const { parentModel, context, childModels } = this;

        // Let's sort models by parent index such that models with lesser index
        // were inserted back first, thus making valid parent index of models following.

        childModels.sort((lhs, rhs) => {
            const
                lhsIndex = context.get(lhs).parentIndex,
                rhsIndex = context.get(rhs).parentIndex;

            return lhsIndex - rhsIndex;
        });

        // Now let's re-insert records back to where they were
        childModels.forEach(m => {
            const ctx = context.get(m);

            parentModel.insertChild(m, ctx.parentIndex, undefined, { orderedParentIndex : ctx.orderedParentIndex });
        });
    }

    redo() {
        this.parentModel.removeChild(this.childModels);
    }
}
